// ADD WALLET (UI rebuild batch 5, Astra's `wallet-add`) — a focused view of its own, not a form above the register: "Use a separate compact form and a
// confirmation naming owner, network and address". Its fields, its party search and its lock are Screens' round-6 add form (branch
// screens/wallet-register, 538a2ff), unchanged in what they do:
//
//   · the party picker asks the books about the TYPED text (usePartySearch), says when its list is cut (rosterCut → `more`), asks nothing while the box
//     is empty; a wallet belongs to a party that was READ and matched — typed text is not one, and a party found among the rows a failed refresh kept is
//     not a read (the save waits, says what it waits for, and the post itself refuses);
//   · the retire tick counts this party's hand-registered addresses in use on the chain, as the add door's tick retires them;
//   · while the request is out EVERY input and button is locked — the lock is the SCREEN's (`busy`), set by the mutation's own callbacks, so the success
//     that closes the view loses nothing typed and a Back-and-reopen cannot be closed by the old request's answer.
//
// ADDED here (Astra's content recommendations for wallet-add): Review wallet → a readback naming the owner (desk or party), the network and the WHOLE
// address before the one Save; a look-alike of an address already in the register is said once, in red, and the same address already registered in
// amber — information only, neither refuses (the add door has its own refusal for a duplicate). A refusal or a lost answer is ServerLine's: a 5xx or a
// dropped connection MAY have saved, and says so whole.
//
// DESK OR PARTY FIRST (his rule, 4 Nov 2026 — lib/desk-wallet.ts): a desk wallet goes under its group · network, its
// network fixed by it and no label typed (the name is built); a customer's or a merchant's under them, as before. A folder
// of desk accounts ("Crypto office assets") is offered on neither side.
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, type RegisterWallet } from '../../../lib/api';
import { readState } from '../../../lib/read-state';
import { tg } from '../../../lib/telegram';
import { WALLET_CHAINS } from '../../../lib/walletStates';
import { rosterCut, usePartySearch } from '../../../lib/roster-cut';
import { Field, FieldRow } from '../../Field';
import { Combobox, type ComboboxOption } from '../../Combobox';
import { Fact, Panel, ReadFailed, ServerLine, StateLine } from '../kit';
import { lookalikeIn, sameIn } from './view';
import { deskAccounts } from '../../../lib/desk-wallet';

export function AddWallet({ wallets, busy, setBusy, gold, touch, onDone }: {
  wallets: readonly RegisterWallet[];
  busy: boolean;
  setBusy: (on: boolean) => void;
  /** This form owns the screen's one gold button. */
  gold: boolean;
  touch: () => void;
  /** Saved: what to say on the register. */
  onDone: (words: string) => void;
}) {
  const [whose, setWhose] = useState<'desk' | 'party'>('party');
  const [slug, setSlug] = useState('');
  const [pickedChain, setChain] = useState<typeof WALLET_CHAINS[number]>('TRC20');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [retirePrev, setRetirePrev] = useState(false);
  // Review wallet: the readback before the one Save (Astra). Edit returns to the fields with everything typed kept.
  const [reviewing, setReviewing] = useState(false);
  const parties = usePartySearch({ base: ['parties', 'for-wallets'], text: slug, ask: (q) => api.listParties({ q }), enabled: whose === 'party' && slug.length > 0 });
  // The desk's accounts are few and read whole: every group · network is offered before anything is typed.
  const ownAccounts = useQuery({
    queryKey: ['parties', 'own-accounts', 'for-wallets'],
    queryFn:  () => api.listParties({ type: 'own_account', pageSize: 500 }),
    enabled:  whose === 'desk',
  });
  const lookup = whose === 'desk' ? readState(ownAccounts) : readState(parties);
  const desk = useMemo(() => deskAccounts(ownAccounts.data?.parties ?? []), [ownAccounts.data]);
  const rows = parties.data?.parties ?? [];
  const options: ComboboxOption[] = useMemo(() => (whose === 'desk'
    ? desk.map((a) => ({ value: a.slug, label: a.name, group: a.chain ? 'Group · network' : 'Other desk accounts' }))
    : rows.filter((p) => p.party_type !== 'own_account')
      .map((p) => ({ value: p.slug, label: p.display_name || p.slug, secondary: p.party_type.replace('_', ' ') }))
      .sort((a, b) => a.label.localeCompare(b.label))), [whose, desk, parties.data]);   // eslint-disable-line react-hooks/exhaustive-deps
  // A wallet belongs to a party the books READ and matched (Appendix B: a guard needs 'known').
  const deskPicked = whose === 'desk' && lookup === 'known' ? (desk.find((a) => a.slug === slug) ?? null) : null;
  const partyPicked = whose === 'party' && lookup === 'known' ? (rows.find((p) => p.slug === slug && p.party_type !== 'own_account') ?? null) : null;
  const picked = deskPicked ? { slug: deskPicked.slug, name: deskPicked.name } : partyPicked ? { slug: partyPicked.slug, name: partyPicked.display_name || partyPicked.slug } : null;
  // A group · network fixes the network: "Hot · ERC20" takes ERC20 addresses only.
  const fixed = deskPicked?.chain ?? null;
  const chain = fixed ?? pickedChain;
  const pick = (w: 'desk' | 'party') => { setWhose(w); setSlug(''); setLabel(''); setRetirePrev(false); };
  // The same count the add door's tick retires: this party's hand-registered addresses in use on this chain.
  const others = wallets.filter((w) => w.party_slug === slug && w.chain === chain && w.status === 'active' && !w.from_ledger_key).length;
  const willRetire = retirePrev && others > 0;
  // What still stops the review, in words (the operator, 18 Oct 2026: "why not able to save this" — the party was empty and the button only dimmed).
  const missing = !slug ? (whose === 'desk' ? 'Pick the group · network.' : 'Pick the party — search by name.')
    : !picked ? (lookup === 'failed' ? `The ${whose === 'desk' ? 'desk accounts' : 'party list'} could not be read — try again.` : lookup === 'waiting' ? 'Reading the parties…' : `Pick the ${whose === 'desk' ? 'group · network' : 'party'} from the list.`)
    : !address.trim() ? 'Paste the address.' : null;
  const twin = useMemo(() => (reviewing ? lookalikeIn(wallets, chain, address) : null), [reviewing, wallets, chain, address]);
  const same = useMemo(() => (reviewing ? sameIn(wallets, chain, address) : null), [reviewing, wallets, chain, address]);
  const add = useMutation({
    onMutate: () => setBusy(true),
    onSettled: () => setBusy(false),
    mutationFn: () => {
      if (!picked) throw new Error('party not read');   // every door that books goes through here, not only the button
      return api.addPartyWallet(slug, { chain, address: address.trim(), label: whose === 'party' ? label.trim() || undefined : undefined, retire_previous_on_chain: willRetire });
    },
    onSuccess: (r) => {
      tg()?.HapticFeedback.notificationOccurred('success');
      const who = picked?.name || slug;
      const retired = r.retired_previous_count > 0 ? ` ${r.retired_previous_count} retired — still watched.` : '';
      onDone(`${r.wallet.chain} address added for ${who}.${retired}`);
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });
  const primary = `${gold ? 'btn-primary' : 'btn-secondary'} w-full min-h-11 px-5 text-body disabled:opacity-50 lg:min-h-10 lg:w-auto`;

  return (
    <Panel className="!overflow-visible lg:max-w-[600px]">
      <div className="space-y-4" onFocusCapture={touch}>
        {!reviewing ? (
          // The fields — locked whole while a request is out (a disabled fieldset takes every input and button in it, the picker's list too).
          <fieldset disabled={busy} className="space-y-4">
            <div className="grid grid-cols-2 gap-2 lg:max-w-xs" role="group" aria-label="Whose wallet">
              {(['desk', 'party'] as const).map((w) => (
                <button key={w} type="button" aria-pressed={whose === w} data-whose={w} onClick={() => pick(w)}
                  className={`seg min-h-11 text-body lg:min-h-10 ${whose === w ? 'seg-on' : ''}`}>{w === 'desk' ? 'Desk' : 'Party'}</button>
              ))}
            </div>
            <FieldRow>
              <Field label={whose === 'desk' ? 'Group · network' : 'Party'}>
                <Combobox
                  key={whose}
                  value={slug} onChange={setSlug} options={options} placeholder={whose === 'desk' ? 'Hot · ERC20, …' : 'Search party'}
                  more={whose === 'desk' ? rosterCut(ownAccounts.data) : rosterCut(parties.data)}
                  loading={(whose === 'desk' || slug.length > 0) && lookup === 'waiting'}
                  emptyHint={whose === 'party' && slug.length === 0 ? null : undefined}
                  readFailed={lookup === 'failed'
                    ? (whose === 'desk'
                      ? <ReadFailed what="the desk accounts" error={ownAccounts.error} onRetry={() => void ownAccounts.refetch()} />
                      : <ReadFailed what="the parties" error={parties.error} onRetry={() => void parties.refetch()} />)
                    : undefined}
                />
              </Field>
              <Field label="Network">
                {fixed
                  ? <div className="flex min-h-11 items-center text-body lg:min-h-10" data-field="chain">{fixed}</div>
                  : (
                    <select className="input w-full disabled:opacity-60" value={chain} data-field="chain"
                      onChange={(e) => setChain(e.target.value as typeof WALLET_CHAINS[number])}>
                      {WALLET_CHAINS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Address">
                <input className="input w-full font-mono disabled:opacity-60" value={address} data-field="address" onChange={(e) => setAddress(e.target.value)}
                  placeholder="The full address" />
              </Field>
              {whose === 'party' && (
                <Field label="Label" hint="optional">
                  <input className="input w-full disabled:opacity-60" value={label} data-field="label" onChange={(e) => setLabel(e.target.value)} />
                </Field>
              )}
            </FieldRow>
            {others > 0 && (
              <label className="flex min-h-11 items-center gap-2 text-meta lg:min-h-0">
                <input type="checkbox" checked={retirePrev} onChange={(e) => setRetirePrev(e.target.checked)} />
                <span className="text-tg-hint">Retire the other {others} {chain} in use</span>
              </label>
            )}
            <div className="flex flex-col gap-2 border-t border-stroke-soft pt-4 lg:flex-row-reverse lg:items-center lg:justify-between">
              <button type="button" className={primary} disabled={!!missing} onClick={() => setReviewing(true)}>Review wallet →</button>
              {missing && <p className="text-meta text-tg-hint" data-why-not="">{missing}</p>}
            </div>
          </fieldset>
        ) : (
          <>
            <div>
              <Fact label="Owner">{picked ? (whose === 'desk' ? picked.name : <>{picked.name} · Party</>) : slug}</Fact>
              <Fact label="Network">{chain}</Fact>
              <Fact label="Address"><span className="break-all font-mono">{address.trim()}</span></Fact>
              {whose === 'party' && <Fact label="Label">{label.trim() || '—'}</Fact>}
              {willRetire && <Fact label="Also retires">{others} {chain} in use — still watched</Fact>}
            </div>
            {twin && <StateLine tone="bad" more={<span className="break-all font-mono">{twin.address}</span>}>Looks like {twin.party_name ?? twin.party_slug}&rsquo;s address — check every character.</StateLine>}
            {same && <StateLine tone="warn">Already in the register for {same.party_name ?? same.party_slug}.</StateLine>}
            <div className="flex flex-col gap-2 border-t border-stroke-soft pt-4 lg:flex-row-reverse lg:items-center lg:justify-between">
              <button type="button" className={primary} disabled={!!missing || busy} onClick={() => add.mutate()}>
                {busy ? 'Saving…' : willRetire ? `Save and retire ${others}` : 'Save wallet'}
              </button>
              <button type="button" className="btn-secondary min-h-11 px-4 text-body disabled:opacity-50 lg:min-h-10" disabled={busy} onClick={() => { add.reset(); setReviewing(false); }}>Edit</button>
            </div>
            {missing && !busy && <p className="text-meta text-tg-hint" data-why-not="">{missing}</p>}
            {add.isError && <ServerLine error={add.error} />}
          </>
        )}
      </div>
    </Panel>
  );
}
